import React from 'react';
import { getClubHandleFromCartItem } from '@/lib/utils';
import type { CartItemResponse, AddOnResponse, ItemStatusCode } from '@/features/cart/types';
import { ItemStatusCode as ItemStatusCodeConst } from '@/features/cart/types';
import { getAddOnById, getAddOnStatus, getGuestsWithAddOn } from '@/features/cart/utils/cartItemUtils';

import { AddOnCard } from '@/components/ui/AddOnCard';
import { HorizontalScrollContainer } from '@/components/ui/HorizontalScrollContainer';

interface CartItemAddOnsDisplayProps {
  cartItem: CartItemResponse;
  addOns: AddOnResponse[];
  addOnStatuses: Array<{ add_on_id: string; status_code: ItemStatusCode }>;
  onAddOnClick: (addOnId: string) => void;
  clubHandle?: string | null;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

const CartItemAddOnsDisplay: React.FC<CartItemAddOnsDisplayProps> = ({
  cartItem,
  addOns,
  addOnStatuses,
  onAddOnClick,
  clubHandle: propClubHandle,
  currency,
}) => {
  // Use provided club handle, or fallback to extracting from cart item/add-ons
  let clubHandle = propClubHandle || getClubHandleFromCartItem(cartItem);
  if (!clubHandle && addOns && addOns.length > 0) {
    clubHandle = addOns[0]?.business?.handle || null;
  }
  // const { formatPrice } = useBusinessMetadataContext();

  // Check if add_ons_per_guest exists and has any non-empty arrays
  if (!cartItem.add_ons_per_guest || !cartItem.add_ons_per_guest.some(guestAddOns => guestAddOns && guestAddOns.length > 0)) {
    return null;
  }

  // Get unique add-on IDs
  const uniqueAddOnIds = Array.from(new Set(
    cartItem.add_ons_per_guest
      .filter(guestAddOns => guestAddOns != null)
      .flatMap(guestAddOns => 
        guestAddOns.map(ao => ao.add_on_id)
      )
  ));

  return (
    <div className="mb-6">
      <HorizontalScrollContainer title="Add-ons">
        {uniqueAddOnIds.map((addOnId) => {
          const guestsWithAddOn = getGuestsWithAddOn(cartItem, addOnId, false);
          if (guestsWithAddOn.length === 0) return null;
          
          const addOnData = getAddOnById(addOns, addOnId);
          const addOnStatus = getAddOnStatus(addOnStatuses, addOnId);
          const isUnavailable = addOnStatus === ItemStatusCodeConst.AddOnDisabled || 
                                addOnStatus === ItemStatusCodeConst.AddOnUnavailable;
          const hasInsufficientInventory = addOnStatus === ItemStatusCodeConst.AddOnInsufficientInventory;
          
          // Calculate total price and total quantity for this add-on across all guests
          let totalPrice = 0;
          let totalQuantity = 0;
          cartItem.add_ons_per_guest.forEach((guestAddOns) => {
            if (!guestAddOns) return;
            const selectedAddOn = guestAddOns.find(ao => ao.add_on_id === addOnId);
            if (selectedAddOn) {
              const qty = selectedAddOn.quantity || 1;
              totalQuantity += qty;
              totalPrice += selectedAddOn.price * qty;
            }
          });

          // Determine status badge text
          let statusBadge: string | undefined;
          if (isUnavailable || hasInsufficientInventory) {
            statusBadge = 'Unavailable';
          }

          return (
            <AddOnCard
              key={addOnId}
              imageId={addOnData?.image_id}
              name={addOnData?.name || 'Unknown'}
              price={totalPrice}
              quantity={totalQuantity}
              currency={currency}
              onClick={() => !isUnavailable && !hasInsufficientInventory && onAddOnClick(addOnId)}
              statusBadge={statusBadge}
              disabled={isUnavailable || hasInsufficientInventory}
              className={(isUnavailable || hasInsufficientInventory) ? 'ring-1 ring-red-500/50' : ''}
            />
          );
        }).filter(Boolean)}
      </HorizontalScrollContainer>
    </div>
  );
};

export default CartItemAddOnsDisplay;
